import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from "randmarcomps";
import {
  ArrowRight,
  Layers,
  Loader2,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";

type StepDefinition = {
  id: string;
  title: string;
  description: string;
  helper: string;
  icon: LucideIcon;
  comingSoon?: boolean;
};

const steps: StepDefinition[] = [
  {
    id: "define",
    title: "Define the offer",
    description:
      "Name the opportunity, confirm the effective dates, and set clear guardrails.",
    helper:
      "Start with the reseller's business objective and work backward from the margin they need to win.",
    icon: Sparkles,
  },
  {
    id: "resellers",
    title: "Select qualified resellers",
    description:
      "Choose who gets access and save them as your default crew for the next offer.",
    helper:
      "Use profiling tags to double-check their competencies before they see your pricing.",
    icon: Users,
  },
  {
    id: "pricing",
    title: "Share tailored pricing",
    description:
      "Attach products, confirm bid pricing, and preview the notification email.",
    helper:
      "Hint: lead with the differentiator - bundle attach rates go up 18% when you do.",
    icon: Layers,
  },
  {
    id: "insights",
    title: "Automate nudges",
    description:
      "Schedule follow-ups and let Randmar ping resellers while you stay focused.",
    helper:
      "Automations will fan out updates, so you only jump in when engagement dips.",
    icon: ShieldCheck,
    comingSoon: true,
  },
];

const quickFacts = [
  { label: "Avg. launch time", value: "~3 min" },
  { label: "Default reseller slots", value: "Unlimited" },
  { label: "Notifications", value: "In-app + email" },
];

const CreateNewOpportunitiesSteps: React.FC = () => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const [activeStepId, setActiveStepId] = React.useState(() => steps[0].id);
  const [ctaBusy, setCtaBusy] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeStep = React.useMemo(
    () => steps.find((step) => step.id === activeStepId) ?? steps[0],
    [activeStepId]
  );

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleStepSelect = React.useCallback((step: StepDefinition) => {
    if (step.comingSoon) {
      return;
    }
    setActiveStepId(step.id);
  }, []);

  const handleCreateOpportunityClick = React.useCallback(() => {
    if (ctaBusy || !applicationId) {
      return;
    }
    setCtaBusy(true);
    timeoutRef.current = setTimeout(() => {
      navigate(Path.createOpportunity(applicationId));
    }, 260);
  }, [applicationId, ctaBusy, navigate]);

  return (
    <section
      aria-labelledby="opportunities-empty-title"
      className="space-y-6"
      data-component="opportunities-empty-state"
    >
      <Card className="overflow-hidden border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500/60 via-fuchsia-500/60 to-indigo-500/60"
        />
        <CardHeader className="space-y-3 pb-0">
          <CardTitle
            id="opportunities-empty-title"
            className="text-3xl font-semibold text-slate-900"
          >
            Your first Opportunity is minutes away
          </CardTitle>
          <CardDescription className="text-base leading-relaxed text-slate-600">
            We built a guided checklist so you can publish pricing, invite
            resellers, and keep the conversation moving without leaving this
            page.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-8 px-6 pb-6 pt-4 md:px-8 lg:grid-cols-[1.7fr_1fr]">
          <div>
            <ol className="space-y-4" role="list" aria-label="Opportunity steps">
              {steps.map((step, index) => {
                const isActive = activeStepId === step.id;
                const helperAssistiveId = `step-${step.id}-assistive`;
                const panelId = `step-${step.id}-panel`;
                const StepIcon = step.icon;

                return (
                  <li key={step.id} className="space-y-2">
                    <button
                      type="button"
                      onClick={() => handleStepSelect(step)}
                      disabled={step.comingSoon}
                      aria-controls={panelId}
                      aria-describedby={helperAssistiveId}
                      aria-pressed={isActive}
                      className={[
                        "group flex w-full items-start gap-4 rounded-[var(--surface-radius-xl)] border bg-white/70 p-4 text-left shadow-sm transition",
                        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ops-focus-ring)]",
                        "motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-lg",
                        isActive
                          ? "border-[var(--ops-accent)] shadow-lg"
                          : "border-[var(--ops-border)] hover:border-[var(--ops-border-strong)]",
                        step.comingSoon
                          ? "cursor-not-allowed opacity-65 motion-safe:hover:translate-y-0"
                          : "",
                      ].join(" ")}
                      style={{
                        transition:
                          "transform var(--transition-medium) var(--ease-out), border-color var(--transition-medium) var(--ease-out), box-shadow var(--transition-medium) var(--ease-out), background var(--transition-medium) var(--ease-out)",
                      }}
                    >
                      <span
                        className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--ops-accent-muted)] text-sm font-semibold text-[var(--ops-accent)]"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div className="flex flex-1 flex-col gap-1">
                        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                          Step {index + 1}
                          <span
                            aria-hidden="true"
                            className="h-1 w-1 rounded-full bg-slate-300"
                          />
                          <StepIcon
                            className="h-4 w-4 text-[var(--ops-accent)]"
                            aria-hidden="true"
                          />
                        </div>
                        <p className="text-base font-semibold text-slate-900">
                          {step.title}
                        </p>
                        <p className="text-sm text-slate-600">
                          {step.description}
                        </p>
                      </div>
                      <ArrowRight
                        aria-hidden="true"
                        className={[
                          "h-4 w-4 flex-shrink-0 transition-transform",
                          isActive ? "translate-x-1 text-[var(--ops-accent)]" : "text-slate-400",
                        ].join(" ")}
                      />
                      {step.comingSoon && (
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
                          Coming soon
                        </span>
                      )}
                      <span id={helperAssistiveId} className="sr-only">
                        {step.helper}
                      </span>
                    </button>
                    <div
                      id={panelId}
                      role="status"
                      aria-live={isActive ? "polite" : undefined}
                      className={[
                        "rounded-[var(--surface-radius-xl)] border border-dashed border-[var(--ops-border)] bg-[var(--ops-surface)] px-4 py-3 text-sm text-slate-600 transition",
                        isActive
                          ? "opacity-100"
                          : "invisible h-0 opacity-0",
                      ].join(" ")}
                      style={{
                        transition:
                          "opacity var(--transition-medium) var(--ease-out), visibility var(--transition-medium) var(--ease-out), height var(--transition-medium) var(--ease-out)",
                      }}
                    >
                      {step.helper}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <aside
            role="complementary"
            aria-live="polite"
            className="flex flex-col gap-5 rounded-[var(--surface-radius-xl)] border border-[var(--ops-border)] bg-[var(--ops-card-muted)] p-6 text-slate-900"
          >
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Guided launch
              </p>
              <p className="text-lg font-semibold">{activeStep.title}</p>
              <p className="text-sm text-slate-600">{activeStep.helper}</p>
            </div>

            <Separator />

            <dl className="grid gap-3 text-sm">
              {quickFacts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex items-center justify-between rounded-2xl border border-transparent bg-white/60 px-3 py-2"
                >
                  <dt className="text-slate-500">{fact.label}</dt>
                  <dd className="font-semibold text-slate-900">{fact.value}</dd>
                </div>
              ))}
            </dl>

            <div className="space-y-3 pt-1">
              <Button
                className="w-full justify-center"
                onClick={handleCreateOpportunityClick}
                disabled={ctaBusy || !applicationId}
                aria-live="assertive"
                aria-busy={ctaBusy}
              >
                {ctaBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Preparing builder...
                  </>
                ) : (
                  <>
                    Launch Opportunity
                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-center text-slate-600 hover:text-slate-900"
                asChild
              >
                <a
                  href="https://randmar.io/resources/opportunities"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Learn how Opportunities work (opens in a new tab)"
                >
                  Learn how Opportunities work
                </a>
              </Button>
            </div>
          </aside>
        </CardContent>
      </Card>
    </section>
  );
};

export default CreateNewOpportunitiesSteps;
